import { v4 as uuidv4 } from "uuid";
import {
  CreateDomain,
  CreateDomainPort,
  CreateDomainPortRoute,
  CreateDomainSpec,
} from "../../../../schema/types/domain/create";
import { ngParseLink } from "../../../../utils/linkParser/linkParser";
import {
  DomainInput,
  DomainInputRoutingMode,
  DomainPortCorsAllowOriginInput,
  DomainPortCorsInput,
  DomainPortInput,
  DomainPortRouteMirrorInput,
  DomainPortRouteInput,
  DomainPortTlsInput,
  NameValuePair,
} from "../types/input";
import { defaultCiphers } from "../../../../mst/kinds/domain/ciphers";
import { Domain, DomainPort, DomainPortRoute, DomainPortTls } from "../../../../schema/types/domain/entity";

export function getDefaultDomainPortCors(): DomainPortCorsInput {
  return {
    isEnabled: false,
    allowCredentials: false,
    allowHeaders: [],
    allowMethods: [],
    allowOrigins: [],
    exposeHeaders: [],
    maxAge: "",
  };
}

export function getDefaultDomainPortTls(): DomainPortTlsInput {
  return {
    isEnabled: false,
    cipherSuites: defaultCiphers,
    clientCertificate: {
      isEnabled: false,
      secret: {
        name: "",
        link: "",
      },
    },
    serverCertificate: {
      isEnabled: false,
      secret: {
        name: "",
        link: "",
      },
    },
    minProtocolVersion: "TLSV1_2",
  };
}

export function getDefaultDomainPortRouteInput(): DomainPortRouteInput {
  return {
    id: uuidv4(),
    headers: [],
    hostType: "prefix",
    prefixType: "prefix",
    hostPrefix: "",
    hostRegex: "",
    port: "",
    prefix: "",
    regex: "",
    replacePrefix: "",
    replica: "",
    workload: {
      name: "",
      link: "",
    },
    mirror: [],
  };
}

export function getDefaultDomainPortRouteMirrorInput(): DomainPortRouteMirrorInput {
  return {
    id: uuidv4(),
    workload: {
      name: "",
      link: "",
    },
    percent: "100",
  };
}

export function getDefaultDomainCorsAllowOriginInput(): DomainPortCorsAllowOriginInput {
  return {
    id: uuidv4(),
    type: "exact",
    value: "",
  };
}

export function getDefaultDomainPortInput(): DomainPortInput {
  return {
    id: uuidv4(),
    number: "443",
    protocol: "http2",
    routes: [],
    tls: getDefaultDomainPortTls(),
    cors: getDefaultDomainPortCors(),
  };
}

export function getDefaultDomainInput(): DomainInput {
  return {
    mode: "basic",
    isEdit: false,
    isLoading: false,
    lastPath: "",
    portActiveKey: "",
    gvc: {
      name: "",
      link: "",
      alias: "",
    },
    dryRun: {
      triggerer: "initial",
      apex: {
        isLoading: false,
        value: {},
      },
      raw: {
        isLoading: false,
        value: {},
      },
      nameOnly: {
        isLoading: false,
        value: {},
      },
    },
    basic: {
      name: "",
      description: "",
      workload: {
        name: "",
        link: "",
      },
    },
    advanced: {
      name: "",
      description: "",
      tags: [],
      spec: {
        acceptAll: "none",
        certChallengeType: "http01",
        dnsMode: "cname",
        workload: {
          name: "",
          link: "",
        },
        ports: [],
        routingMode: "pathBased",
      },
    },
  };
}

export function domainBasicToAdvanced(currentValues: DomainInput) {
  const basicInput: DomainInput = JSON.parse(JSON.stringify(currentValues));

  const portId = uuidv4();

  const advancedInput: DomainInput = {
    ...getDefaultDomainInput(),
    ...basicInput,
    mode: "advanced",
    portActiveKey: portId,
    advanced: {
      name: basicInput.basic?.name || "",
      description: basicInput.basic.description || "",
      tags: [],
      spec: {
        dnsMode: "cname",
        certChallengeType: "http01",
        acceptAll: "none",
        workload: {
          name: "",
          link: "",
        },
        routingMode: "pathBased",
        ports: [
          {
            ...getDefaultDomainPortInput(),
            id: portId,
            number: "443",
            protocol: "http2",
            routes: basicInput.basic.workload.name
              ? [
                  {
                    ...getDefaultDomainPortRouteInput(),
                    prefix: `/`,
                    workload: basicInput.basic.workload,
                  },
                ]
              : [],
          },
        ],
      },
    },
  };

  if (basicInput.basic.workload.link) {
    const { org, gvc } = ngParseLink(basicInput.basic.workload.link, { useInputCtx: true });
    const gvcLink = `/org/${org}/gvc/${gvc}`;
    if (advancedInput.gvc.link !== gvcLink) {
      advancedInput.gvc = {
        link: gvcLink,
        name: gvc,
        alias: "",
      };
    }
  }

  return advancedInput;
}

// TODO move to a common place START
export function inputToTags(input: NameValuePair[]): Record<string, any> {
  const res: Record<string, any> = {};
  for (const pair of input) {
    res[pair.name] = pair.value || "";
  }
  return res;
}
export function tagsToInput(input: Record<string, any>): NameValuePair[] {
  const res: NameValuePair[] = [];
  for (const key of Object.keys(input)) {
    res.push({
      id: uuidv4(),
      name: key,
      value: input[key] || "",
    });
  }
  return res;
}
// TODO move to a common place END

function inputToPort(p: DomainPortInput, d: DomainInput): CreateDomainPort {
  const port: CreateDomainPort = {
    protocol: p.protocol,
    routes: [],
  };

  if (p.number.length > 0) {
    port.number = Number(p.number);
  }

  for (const r of p.routes) {
    const route = inputToRoute(r, d);
    port.routes?.push(route);
  }

  const mustEnableTLS = (!p.number || p.number === "443") && p.protocol !== "tcp";
  if (p.tls.isEnabled || mustEnableTLS) {
    port.tls = {
      minProtocolVersion: p.tls.minProtocolVersion,
      cipherSuites: p.tls.cipherSuites as any[], // can maybe filter out empty items on lists?
    };
    if (p.tls.clientCertificate.isEnabled) {
      port.tls.clientCertificate = {};
      if (p.tls.clientCertificate.secret.name) {
        port.tls.clientCertificate.secretLink = p.tls.clientCertificate.secret.link;
      }
    }
    if (p.tls.serverCertificate.isEnabled) {
      port.tls.serverCertificate = {};
      if (p.tls.serverCertificate.secret.name) {
        port.tls.serverCertificate.secretLink = p.tls.serverCertificate.secret.link;
      }
    }
  }

  if (p.cors.isEnabled) {
    port.cors = {
      allowOrigins: p.cors.allowOrigins.map((ao) => {
        if (ao.type === "regex") {
          return { regex: ao.value };
        }
        return { exact: ao.value };
      }),
      allowCredentials: p.cors.allowCredentials,
      allowHeaders: p.cors.allowHeaders,
      exposeHeaders: p.cors.exposeHeaders,
      allowMethods: p.cors.allowMethods,
    };
    if (p.cors.maxAge.length > 0) {
      port.cors.maxAge = p.cors.maxAge;
    }
  }
  return port;
}

function inputToRoute(r: DomainPortRouteInput, d: DomainInput): CreateDomainPortRoute {
  let workloadLink = "";
  const s = d.advanced.spec;
  if (s.routingMode === "workloadSubdomain") {
    workloadLink = s.workload.link!;
  } else {
    workloadLink = `//gvc/${d.gvc.name}/workload/${r.workload.name}`;
  }

  const route: CreateDomainPortRoute = {
    workloadLink: workloadLink,
  };

  if (r.prefixType === "regex" && r.regex.length > 0) {
    route.regex = r.regex;
  } else if (r.prefixType === "prefix" && r.prefix.length > 0) {
    route.prefix = r.prefix;
  }

  if (s.acceptAll !== "none") {
    if (r.hostType === "regex" && r.hostRegex.length > 0) {
      route.hostRegex = r.hostRegex;
    } else if (r.hostType === "prefix" && r.hostPrefix.length > 0) {
      route.hostPrefix = r.hostPrefix;
    }
  }

  if (r.port.length > 0) {
    route.port = Number(r.port);
  }
  if (r.replica.length > 0) {
    route.replica = Number(r.replica);
  }
  if (r.replacePrefix.length > 0) {
    route.replacePrefix = r.replacePrefix;
  }

  const headers = {
    request: {
      set: {},
    },
  };
  const set = {};
  for (const header of r.headers) {
    if (!header.name) {
      continue;
    }
    set[header.name] = header.value;
  }
  headers.request.set = set;

  if (Object.keys(set).length > 0) {
    route.headers = headers;
  }

  if (r.mirror && r.mirror.length > 0) {
    const mirrorEntries = r.mirror
      .filter((m) => m.workload.link)
      .map((m) => ({
        workloadLink: m.workload.link,
        percent: Number(m.percent),
      }));
    if (mirrorEntries.length > 0) {
      route.mirror = mirrorEntries;
    }
  }

  return route;
}

function routeToInput(r: DomainPortRoute): DomainPortRouteInput {
  const { name: workloadName } = ngParseLink(r.workloadLink || "", { useInputCtx: true });

  const route: DomainPortRouteInput = {
    ...getDefaultDomainPortRouteInput(),
    workload: {
      name: workloadName,
      link: r.workloadLink || "",
    },
  };

  if (r.regex && r.regex.length > 0) {
    route.prefixType = "regex";
    route.regex = r.regex;
  } else if (r.prefix && r.prefix.length > 0) {
    route.prefixType = "prefix";
    route.prefix = r.prefix;
  }

  if (r.hostRegex && r.hostRegex.length > 0) {
    route.hostType = "regex";
    route.hostRegex = r.hostRegex;
  } else if (r.hostPrefix && r.hostPrefix.length > 0) {
    route.hostType = "prefix";
    route.hostPrefix = r.hostPrefix;
  }

  if (r.port !== undefined) {
    route.port = String(r.port);
  }
  if (r.replica !== undefined) {
    route.replica = String(r.replica);
  }
  if (r.replacePrefix && r.replacePrefix.length > 0) {
    route.replacePrefix = r.replacePrefix;
  }

  const headers: NameValuePair[] = [];
  const headerSet = r.headers?.request?.set || {};
  for (const [name, value] of Object.entries(headerSet)) {
    headers.push({
      id: uuidv4(),
      name,
      value: String(value),
    });
  }
  route.headers = headers;

  const mirrorInputs: DomainPortRouteMirrorInput[] = [];
  for (const m of r.mirror || []) {
    const parsed = ngParseLink(m.workloadLink || "", { useInputCtx: true });
    mirrorInputs.push({
      id: uuidv4(),
      workload: {
        name: parsed.name,
        link: parsed.absolute,
      },
      percent: String(m.percent),
    });
  }
  route.mirror = mirrorInputs;

  return route;
}

function portToInput(p: DomainPort): DomainPortInput {
  const port: DomainPortInput = {
    ...getDefaultDomainPortInput(),
    number: String(p.number),
    // TODO for example for this, there is actually two domain types on the backend, one if for create with optional, other is after the creation which doesnt have optionals
    protocol: p.protocol!,
    routes: [],
  };

  for (const route of p.routes || []) {
    const routeInput = routeToInput(route);
    port.routes.push(routeInput);
  }

  if (p.tls) {
    port.tls = {
      isEnabled: true,
      minProtocolVersion: p.tls.minProtocolVersion || "TLSV1_2",
      cipherSuites: p.tls.cipherSuites || defaultCiphers,
      clientCertificate: {
        isEnabled: !!p.tls.clientCertificate,
        secret: {
          name: "",
          link: p.tls.clientCertificate?.secretLink || "",
        },
      },
      serverCertificate: {
        isEnabled: !!p.tls.serverCertificate,
        secret: {
          name: "",
          link: p.tls.serverCertificate?.secretLink || "",
        },
      },
    };

    // Extract secret names from links
    if (p.tls.clientCertificate?.secretLink) {
      const { name } = ngParseLink(p.tls.clientCertificate.secretLink, { useInputCtx: true });
      port.tls.clientCertificate.secret.name = name;
    }
    if (p.tls.serverCertificate?.secretLink) {
      const { name } = ngParseLink(p.tls.serverCertificate.secretLink, { useInputCtx: true });
      port.tls.serverCertificate.secret.name = name;
    }
  }

  // Convert CORS
  if (p.cors) {
    port.cors = {
      isEnabled: true,
      allowCredentials: p.cors.allowCredentials || false,
      allowHeaders: p.cors.allowHeaders || [],
      allowMethods: p.cors.allowMethods || [],
      exposeHeaders: p.cors.exposeHeaders || [],
      maxAge: p.cors.maxAge || "",
      allowOrigins: [],
    };

    // Convert allowOrigins
    for (const allowOrigin of p.cors.allowOrigins || []) {
      port.cors.allowOrigins.push({
        id: uuidv4(),
        type: allowOrigin.regex ? "regex" : "exact",
        value: allowOrigin.regex || allowOrigin.exact || "",
      });
    }
  }

  return port;
}

export function inputToDomain(_input: DomainInput): CreateDomain {
  const input: DomainInput = JSON.parse(JSON.stringify(_input));
  if (input.mode === "basic") {
    const b = input.basic;

    const body: CreateDomain = {
      name: b.name,
      description: b.description,
      tags: inputToTags([]),
      kind: "domain",
      spec: {
        dnsMode: "cname",
        certChallengeType: "http01",
        ports: [
          {
            number: 443,
            protocol: "http2",
            routes: [
              {
                workloadLink: b.workload.link,
                prefix: "/",
              },
            ],
          },
        ],
      },
    };

    if (input.isEdit) {
      body["$replace/spec"] = body.spec;
      // @ts-ignore
      delete body.spec;
    }

    return body;
  }

  const a = input.advanced;

  const domain: CreateDomain = {
    name: a.name,
    description: a.description,
    kind: "domain",
    tags: inputToTags(a.tags),
    spec: {},
  };

  const s = a.spec;

  const spec: CreateDomainSpec = {
    acceptAllHosts: s.acceptAll === "hosts",
    acceptAllSubdomains: s.acceptAll === "subdomains",
    certChallengeType: s.certChallengeType,
    dnsMode: s.dnsMode,
  };
  if (s.routingMode === "gvcSubdomain") {
    spec.gvcLink = input.gvc.link;
  }
  if (s.routingMode === "workloadSubdomain") {
    spec.workloadLink = s.workload.link;
  }

  if (s.routingMode !== "none") {
    const ports: CreateDomainPort[] = [];
    for (const p of s.ports) {
      const port = inputToPort(p, input);
      ports.push(port);
    }
    spec.ports = ports;
  }
  domain.spec = spec;

  if (input.isEdit) {
      domain["$replace/spec"] = domain.spec;
      // @ts-ignore
      delete domain.spec;
    }

  return domain;
}

export function domainToInput(domain: Domain): DomainInput {
  let isAdvanced = false;
  if (domain.spec?.dnsMode !== "cname") {
    isAdvanced = true;
  }
  if (domain.spec?.certChallengeType !== "http01") {
    isAdvanced = true;
  }
  if (!!domain.spec?.gvcLink) {
    isAdvanced = true;
  }
  if (!!domain.spec?.workloadLink) {
    isAdvanced = true;
  }
  if (!!domain.spec?.acceptAllHosts) {
    isAdvanced = true;
  }
  if (!!domain.spec?.acceptAllSubdomains) {
    isAdvanced = true;
  }
  if (domain.spec?.ports?.length !== 1) {
    isAdvanced = true;
  }
  for (const port of domain.spec?.ports || []) {
    if (port.number !== 443) {
      isAdvanced = true;
    }
    if (port.protocol !== `http2`) {
      isAdvanced = true;
    }
    if (port.routes?.length !== 1) {
      isAdvanced = true;
    }
    for (const route of port.routes || []) {
      if (Object.keys(route.headers?.request?.set || {}).length > 0) {
        isAdvanced = true;
      }
      if (!!route.hostPrefix) {
        isAdvanced = true;
      }
      if (!!route.hostRegex) {
        isAdvanced = true;
      }
      if (!!route.port) {
        isAdvanced = true;
      }
      if (route.prefix !== `/`) {
        isAdvanced = true;
      }
      if (!!route.regex) {
        isAdvanced = true;
      }
      if (!!route.replacePrefix) {
        isAdvanced = true;
      }
      if (route.replica !== undefined) {
        isAdvanced = true;
      }
      if (route.mirror && route.mirror.length > 0) {
        isAdvanced = true;
      }
      if (!route.workloadLink) {
        isAdvanced = true;
      }
    }

    if (!isTlsDefault(port.tls)) {
      isAdvanced = true;
    }

    if (!!port.cors) {
      isAdvanced = true;
    }
  }

  if (!isAdvanced) {
    const { absolute, name } = ngParseLink(domain.spec?.ports![0].routes![0].workloadLink!, { useInputCtx: true });
    const form: DomainInput = {
      ...getDefaultDomainInput(),
      mode: "basic",
      isEdit: true,
      basic: {
        name: domain.name,
        description: domain.description,
        workload: {
          name: name,
          link: absolute,
        },
      },
    };
    return form;
  }

  let routingMode: DomainInputRoutingMode = "none";
  if (domain.spec?.gvcLink) {
    routingMode = "gvcSubdomain";
  } else if (domain.spec?.workloadLink) {
    routingMode = "workloadSubdomain";
  } else if ((domain.spec?.ports || []).some((p) => (p.routes || []).length > 0)) {
    // TODO we can check here more things
    routingMode = "pathBased";
  }

  const form: DomainInput = {
    ...getDefaultDomainInput(),
    mode: "advanced",
    isEdit: true,
    advanced: {
      name: domain.name,
      description: domain.description,
      tags: tagsToInput(domain.tags),
      spec: {
        acceptAll: domain.spec?.acceptAllHosts ? "hosts" : domain.spec?.acceptAllSubdomains ? "subdomains" : "none",
        certChallengeType: domain.spec?.certChallengeType || (domain.spec?.dnsMode === "cname" ? "http01" : "dns01"),
        dnsMode: domain.spec?.dnsMode || "cname",
        workload: {
          name: ngParseLink(domain.spec?.workloadLink || "", { useInputCtx: true }).name,
          link: domain.spec?.workloadLink || "",
        },
        routingMode: routingMode,
        ports: (domain.spec?.ports || []).map((p) => portToInput(p)),
      },
    },
  };

  if (routingMode === "pathBased") {
    let done = false;
    for (const port of domain.spec?.ports || []) {
      for (const route of port.routes || []) {
        if (route.workloadLink) {
          done = true;
          const { org, gvc } = ngParseLink(route.workloadLink, { useInputCtx: true });
          form.gvc = {
            name: gvc,
            link: `/org/${org}/gvc/${gvc}`,
            alias: "",
          };
          break;
        }
      }
      if (done) {
        break;
      }
    }
  } else if (routingMode === "gvcSubdomain") {
    const { gvc } = ngParseLink(domain.spec?.gvcLink!, { useInputCtx: true });
    form.gvc = {
      name: gvc,
      link: domain.spec?.gvcLink || "",
      alias: "",
    };
  } else if (routingMode === "workloadSubdomain") {
    const { org, gvc } = ngParseLink(domain.spec?.workloadLink!, { useInputCtx: true });
    form.gvc = {
      name: gvc,
      link: `/org/${org}/gvc/${gvc}`,
      alias: "",
    };
  }

  return form;
}

function isTlsDefault(tls: DomainPortTls | undefined): boolean {
  if (tls) {
    if (tls.cipherSuites?.length !== 8) {
      return false;
    }
    for (const cipherSuite of tls.cipherSuites || []) {
      if (
        ![
          "ECDHE-ECDSA-AES256-GCM-SHA384",
          "ECDHE-ECDSA-CHACHA20-POLY1305",
          "ECDHE-ECDSA-AES128-GCM-SHA256",
          "ECDHE-RSA-AES256-GCM-SHA384",
          "ECDHE-RSA-CHACHA20-POLY1305",
          "ECDHE-RSA-AES128-GCM-SHA256",
          "AES256-GCM-SHA384",
          "AES128-GCM-SHA256",
        ].includes(cipherSuite)
      ) {
        return false;
      }
    }
    if (tls.minProtocolVersion !== "TLSV1_2") {
      return false;
    }
    if (tls.clientCertificate) {
      return false;
    }
    if (tls.serverCertificate) {
      return false;
    }
  }
  return true;
}
